import type { CellarTransfersResponse } from "@corklog/shared";
import { useCallback, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { ApiError, fetchTransfers, offerTransfer, withdrawTransfer } from "../api.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { actionButton, control, fieldLabel, resting } from "../components/controls.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";

/**
 * Task 9.8 — handing the cellar to somebody else.
 *
 * **A page, not a button at the foot of the guest list.** What this does is
 * irreversible for the person doing it, and the only honest way to present it
 * is to say the whole of what happens before anything is typed: the cellar
 * moves entire, every other access to it is revoked — the previous owner's
 * included — and the notes stay attributed to whoever wrote them. That is four
 * sentences, and four sentences do not belong under a checkbox.
 *
 * **Reached from *Gäster*, and not in the row of sections.** The row wraps
 * onto three lines on a phone and refused a tenth entry once on those grounds;
 * *Till serveringen* and *Orden* are the precedent. *Gäster* is the right
 * doorway, because it is already the answer to who reaches this cellar and
 * this is that question at its limit.
 *
 * **Nothing here asks twice.** Offering loses nothing — the cellar stays put
 * until the other person answers, and withdrawing is one tap until they do —
 * so the confirmation this application reserves for deleting a lot is not
 * earned here. What is earned is saying plainly what an acceptance would do,
 * which is what the paragraph above the form is for.
 */
type State =
  | { status: "loading" }
  | { status: "ready"; transfers: CellarTransfersResponse }
  | { status: "notOwner" }
  | { status: "failed" };

/** Dates in the interface's language, like every other date in it. */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString(locale());
}

export function Transfer() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offered, setOffered] = useState<string | null>(null);

  const load = useCallback(() => {
    void fetchTransfers(cellarId)
      .then((transfers) => setState({ status: "ready", transfers }))
      .catch((cause: unknown) => {
        setState(
          cause instanceof ApiError && cause.status === 403
            ? { status: "notOwner" }
            : { status: "failed" },
        );
      });
  }, [cellarId]);

  useReload(load);

  const offer = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim() === "" || busy) return;
    setBusy(true);
    setError(null);
    setOffered(null);
    void offerTransfer(cellarId, email)
      .then((transfer) => {
        // Cleared only on the way out, so an address that was refused stays in
        // the field to be corrected rather than retyped — the rule the invite
        // form already follows.
        setEmail("");
        setOffered(transfer.toName);
        load();
      })
      .catch((cause: unknown) => {
        const status = cause instanceof ApiError ? cause.status : 0;
        setError(
          status === 404
            ? // The one refusal that needed its own sentence: unlike an
              // invitation, this cannot wait for an account, because what it
              // is waiting for is an answer.
              t("transfer.noAccount")
            : status === 400
              ? t("transfer.notAnAddress")
              : status === 409
                ? t("transfer.alreadyOffered")
                : t("transfer.offerFailed"),
        );
      })
      .finally(() => setBusy(false));
  };

  const withdraw = (transferId: string) => {
    setError(null);
    setOffered(null);
    void withdrawTransfer(cellarId, transferId)
      .then(load)
      .catch(() => setError(t("transfer.withdrawFailed")));
  };

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="transfer" />

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "notOwner" && (
        <p className="text-muted mt-6 max-w-prose text-sm">{t("transfer.ownersOnly")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("transfer.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        <>
          {/* What it is, before what to press. Three sentences, because the
              third one — that this is the end of your own access — is the one
              somebody would otherwise find out afterwards. */}
          <p className="text-muted mt-2 max-w-prose text-sm">{t("transfer.intro")}</p>
          <p className="text-muted mt-2 max-w-prose text-sm">{t("transfer.whatMoves")}</p>
          <p className="text-muted mt-2 max-w-prose text-sm">{t("transfer.whatEnds")}</p>

          {state.transfers.pending === null ? (
            <form
              className="border-line mt-8 flex flex-wrap items-end gap-4 border-t pt-6"
              onSubmit={offer}
            >
              <div className="w-72">
                <label className={fieldLabel} htmlFor="transfer-email">
                  {t("transfer.email")}
                </label>
                <input
                  id="transfer-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className={`${control} ${resting} mt-1`}
                />
              </div>
              <button
                type="submit"
                disabled={busy || email.trim() === ""}
                className={actionButton}
              >
                {t("transfer.offer")}
              </button>
            </form>
          ) : (
            /* One offer at a time, so while one is out the form is not drawn
               at all: there is nothing useful to type, and a field that
               answered *already offered* on every press would be a field
               pretending to be available. */
            <div className="border-line mt-8 border-t pt-6">
              <p className="text-ink max-w-prose text-sm">
                {t("transfer.waiting", {
                  name: state.transfers.pending.toName,
                  email: state.transfers.pending.toEmail,
                  date: day(state.transfers.pending.offeredAt),
                })}
              </p>
              <p className="text-muted mt-2 max-w-prose text-sm">
                {t("transfer.waitingExplained")}
              </p>
              <button
                type="button"
                onClick={() => withdraw(state.transfers.pending?.id ?? "")}
                className="text-muted hover:text-past mt-4 text-sm underline-offset-4 hover:underline"
              >
                {t("transfer.withdraw")}
              </button>
            </div>
          )}

          {error !== null && (
            <p role="alert" className="text-past mt-4 text-sm">
              {error}
            </p>
          )}

          {offered !== null && (
            <p role="status" className="text-ink mt-4 max-w-prose text-sm">
              {t("transfer.offerMade", { name: offered })}
            </p>
          )}

          {/* What has already been tried, including the two endings that
              changed nothing. A cellar that has changed hands says so here,
              which is the only place it can be read. */}
          {state.transfers.history.length > 0 && (
            <div className="border-line mt-8 border-t pt-6">
              <h2 className="text-muted text-sm">{t("transfer.historyTitle")}</h2>
              <ul className="mt-2">
                {state.transfers.history.map((row) => (
                  <li key={row.id} className="text-muted py-1 text-sm">
                    {t(`transfer.outcome.${row.outcome ?? "Withdrawn"}`, {
                      name: row.toName,
                      from: row.fromName,
                      date: day(row.settledAt ?? row.offeredAt),
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-muted mt-8 max-w-prose text-sm">
            <Link
              to={`/kallare/${cellarId}/gaster`}
              className="text-accent underline-offset-4 hover:underline"
            >
              {t("transfer.backToGuests")}
            </Link>
          </p>
        </>
      )}
    </section>
  );
}
